import React, { useEffect, useState } from 'react';
import ServerStat from './ServerStat';
import { useServerStatsStore } from '../../../store/serverStats/serverStatsStore';
import { useCustomMetrics } from '../../../hooks/useCustomMetrics';
import { formatBitsPerSecond } from '../../../utils/formatBitsPerSecond';
import { useServerStatsInterval } from '../../../hooks/useServerStatsInterval';
import { getPrometheusMetric } from '../../../api/serverStats/prometheusApi';

interface ServerStatsContainerProps {
    isPageActive?: boolean;
}

const ServerStatsContainer: React.FC<ServerStatsContainerProps> = ({ isPageActive = true }) => {
    const fetchServerStats = useServerStatsStore(s => s.fetchServerStats);
    const serverStats = useServerStatsStore(s => s.serverStats);
    const loading = useServerStatsStore(s => s.loading);
    const { trackServerStats } = useCustomMetrics();
    const [storageUsage, setStorageUsage] = useState(0);

    const { isActive, retryCount } = useServerStatsInterval({
        fetchFunction: fetchServerStats,
        interval: 10000,
        enabled: isPageActive,
        maxRetries: 3,
        retryDelay: 1000
    });

    useEffect(() => {
        const fetchStorageUsage = async () => {
            try {
                const usage = await getPrometheusMetric('((node_filesystem_size_bytes{mountpoint="/mnt/sdb1"} - node_filesystem_avail_bytes{mountpoint="/mnt/sdb1"}) / node_filesystem_size_bytes{mountpoint="/mnt/sdb1"}) * 100');
                setStorageUsage(Math.round(usage));
            } catch (error) {
                console.warn('Error fetching storage usage:', error);
                setStorageUsage(0);
            }
        };

        if (isPageActive) {
            fetchStorageUsage();
            const interval = setInterval(fetchStorageUsage, 10000);
            return () => clearInterval(interval);
        }
    }, [isPageActive]);

    useEffect(() => {
        if (serverStats && !loading) {
            trackServerStats({
                ...serverStats,
                storage_usage: storageUsage
            });
        }
    }, [serverStats, loading, trackServerStats, storageUsage]);

    const statsConfig = [
        {
            title: 'Скорость сети',
            value: !serverStats ? '0 Bps' : formatBitsPerSecond(serverStats.net_speed_download || 0),
            percent: !serverStats ? 0 : Math.round(serverStats.net_load_percent || 0),
            bgColor: '#D6F2EC',
            barColor: '#5EC2B7',
        },
        {
            title: 'Оперативная память',
            percent: !serverStats ? 0 : Math.round(serverStats.ram_usage),
            bgColor: '#FCE9C8',
            barColor: '#F7B94A',
        },
    ];

    const diskStatsConfig = [
        {
            title: 'Системный диск',
            percent: !serverStats ? 0 : Math.round(serverStats.hdd_usage),
            bgColor: '#FAD7D7',
            barColor: '#F25C5C',
        },
        {
            title: 'Хранилище данных',
            percent: storageUsage,
            bgColor: '#FAD7D7',
            barColor: '#F25C5C',
        },
    ];

    const remainingStatsConfig = [
        {
            title: 'Загруженность процессора',
            percent: !serverStats ? 0 : Math.round(serverStats.cpu_usage),
            bgColor: '#D6F2EC',
            barColor: '#5EC2B7',
        },
    ];

    return (
        <div className="server-stats-wrapper">
            {statsConfig.map((stat, index) => (
                <ServerStat
                    key={index}
                    title={stat.title}
                    percent={stat.percent}
                    value={stat.value}
                    bgColor={stat.bgColor}
                    barColor={stat.barColor}
                />
            ))}
            <div className="server-stats-row">
                {diskStatsConfig.map((stat, index) => (
                    <ServerStat
                        key={`disk-${index}`}
                        title={stat.title}
                        percent={stat.percent}
                        bgColor={stat.bgColor}
                        barColor={stat.barColor}
                    />
                ))}
            </div>
            {remainingStatsConfig.map((stat, index) => (
                <ServerStat
                    key={`remaining-${index}`}
                    title={stat.title}
                    percent={stat.percent}
                    bgColor={stat.bgColor}
                    barColor={stat.barColor}
                />
            ))}
        </div>
    );
};

export default ServerStatsContainer; 